const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SEQUENCE_PATTERN = /^(0|[1-9][0-9]{0,18})$/;
const MAX_SEQUENCE = 9_223_372_036_854_775_807;
const MAX_SSE_BUFFER_CHARACTERS = 6 * 1_024 * 1_024;
const MAX_ERROR_BODY_CHARACTERS = 8 * 1_024;
export const MAX_RUN_MESSAGE_UTF8_BYTES = 3_072;
const MAX_RUN_REQUEST_UTF8_BYTES = 4_096;
const MAX_RESUMED_RUN_REQUEST_UTF8_BYTES = 6 * 1_024;
const MAX_HISTORY_CONTENT_UTF8_BYTES = 3_145_712;
const MAX_HISTORY_PAGE_SIZE = 50;
const MAX_HISTORY_TOKEN_CHARACTERS = 2_048;
const MAX_BOOTSTRAP_ERROR_BODY_BYTES = 4 * 1_024;
const MAX_BOOTSTRAP_ERROR_BODY_CHUNKS = 32;
const BROWSER_BOOTSTRAP_TIMEOUT_MILLISECONDS = 5_000;
const CSRF_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
const CSRF_BOOTSTRAP_PATH = "/auth/csrf";

export const ACTIVE_RUN_STATES = new Set<RunState>([
  "QUEUED",
  "RUNNING",
  "STREAMING",
]);

export const TERMINAL_RUN_STATES = new Set<RunState>([
  "SUCCEEDED",
  "FAILED",
  "CANCELLED",
  "REVOKED",
  "EXPIRED",
]);

export type RunState =
  | "QUEUED"
  | "RUNNING"
  | "STREAMING"
  | "SUCCEEDED"
  | "FAILED"
  | "CANCELLED"
  | "REVOKED"
  | "EXPIRED";

export type UsageState =
  | "RESERVED"
  | "PENDING_RECONCILIATION"
  | "RECONCILED";

export type ResultState = "NONE" | "COMPLETE" | "PARTIAL";

export type RunSnapshot = {
  contract_version: "1";
  schema_version: "1.0.0";
  run_id: string;
  correlation_id: string;
  state: RunState;
  deadline_at: string;
  last_sequence: number;
  usage_state: UsageState;
  result_state: ResultState;
  created_at: string;
  updated_at: string;
  terminal_at: string | null;
};

export type RunCommand = {
  contract_version: "1";
  schema_version: "1.0.0";
  run_id: string;
  state: RunState;
  outcome: "APPLIED" | "NO_CHANGE_PENDING" | "NO_CHANGE_TERMINAL";
};

export type ConversationSummary = {
  contract_version: "1";
  schema_version: "1.0.0";
  conversation_id: string;
  first_visible_sequence: number;
  last_visible_sequence: number;
  first_activity_at: string;
  last_activity_at: string;
  next_content_expires_at: string;
};

export type ConversationPage = {
  contract_version: "1";
  schema_version: "1.0.0";
  items: ConversationSummary[];
  next_cursor: string | null;
};

export type AvailableConversationEntry = {
  contract_version: "1";
  schema_version: "1.0.0";
  state: "AVAILABLE";
  entry_id: string;
  conversation_id: string;
  entry_sequence: number;
  role: "USER" | "ASSISTANT";
  content_kind: "MESSAGE" | "COUNTEROPINION_RESULT";
  content: string;
  created_at: string;
  content_expires_at: string;
};

export type RemovedConversationEntry = {
  contract_version: "1";
  schema_version: "1.0.0";
  state: "REMOVED";
  after_sequence: number;
  before_sequence: number;
};

export type ConversationEntry =
  | AvailableConversationEntry
  | RemovedConversationEntry;

export type ConversationEntriesPage = {
  contract_version: "1";
  schema_version: "1.0.0";
  conversation_id: string;
  items: ConversationEntry[];
  next_cursor: string | null;
};

export type ConversationDeletion = {
  contract_version: "1";
  schema_version: "1.0.0";
  entry_id: string;
  disposition: "REQUESTED" | "ALREADY_REQUESTED";
};

export type ConversationResume = {
  contract_version: "1";
  schema_version: "1.0.0";
  resume_id: string;
  conversation_id: string;
  maximum_visible_sequence: number;
  resume_expires_at: string;
  authorization_status: "RECHECK_REQUIRED";
  egress_status: "PENDING_RUN_POLICY";
  resume_token: string;
};

type StreamEventBase = {
  contract_version: "1";
  schema_version: "1.0.0";
  run_id: string;
  correlation_id: string;
  sequence: number;
  occurred_at: string;
};

export type StreamChunkEvent = StreamEventBase & {
  kind: "chunk";
  text: string;
};

export type StreamRunStateEvent = StreamEventBase & {
  kind: "run-state";
  state: RunState;
  result_state: ResultState;
  usage_state: UsageState;
};

export type StreamControlCode =
  | "STREAM_EXPIRED"
  | "STREAM_BACKLOG_EXCEEDED"
  | "STREAM_UNAVAILABLE"
  | "STREAM_WINDOW_CLOSED";

export type StreamControlEvent = {
  kind: "stream-control";
  contract_version: "1";
  schema_version: "1.0.0";
  code: StreamControlCode;
};

export type PortalStreamEvent =
  | StreamChunkEvent
  | StreamRunStateEvent
  | StreamControlEvent;

export class PortalGatewayError extends Error {
  readonly code: string;
  readonly httpStatus: number | null;
  readonly retryable: boolean;
  readonly bootstrapInvalid: boolean;

  constructor({
    code,
    httpStatus = null,
    retryable = false,
    bootstrapInvalid = false,
  }: {
    code: string;
    httpStatus?: number | null;
    retryable?: boolean;
    bootstrapInvalid?: boolean;
  }) {
    super(code);
    this.name = "PortalGatewayError";
    this.code = code;
    this.httpStatus = httpStatus;
    this.retryable = retryable;
    this.bootstrapInvalid = bootstrapInvalid;
  }
}

export type PortalGateway = {
  submitRun(
    message: string,
    signal: AbortSignal,
    resumeToken?: string,
  ): Promise<RunCommand>;
  readActiveRun(signal: AbortSignal): Promise<RunSnapshot | null>;
  readRun(runId: string, signal: AbortSignal): Promise<RunSnapshot>;
  streamRun(
    runId: string,
    afterSequence: number,
    signal: AbortSignal,
  ): AsyncIterable<PortalStreamEvent>;
  revokeRun(runId: string, signal: AbortSignal): Promise<RunCommand>;
  listConversations(
    cursor: string | null,
    signal: AbortSignal,
  ): Promise<ConversationPage>;
  listConversationEntries(
    conversationId: string,
    cursor: string | null,
    signal: AbortSignal,
  ): Promise<ConversationEntriesPage>;
  deleteConversationEntry(
    entryId: string,
    signal: AbortSignal,
  ): Promise<ConversationDeletion>;
  prepareConversationResume(
    conversationId: string,
    signal: AbortSignal,
  ): Promise<ConversationResume>;
};

type FetchLike = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasExactKeys = (
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean => {
  const actual = Object.keys(value);
  return (
    actual.length === keys.length &&
    keys.every((key) => Object.hasOwn(value, key))
  );
};

const isUuidV4 = (value: unknown): value is string =>
  typeof value === "string" && UUID_V4_PATTERN.test(value);

const isDateTime = (value: unknown): value is string =>
  typeof value === "string" && Number.isFinite(Date.parse(value));

const isAwareDateTime = (value: unknown): value is string =>
  isDateTime(value) &&
  /^\d{4}-\d{2}-\d{2}T.+(?:Z|[+-]\d{2}:\d{2})$/i.test(value);

const isSequence = (value: unknown): value is number =>
  typeof value === "number" &&
  Number.isSafeInteger(value) &&
  value >= 0 &&
  value <= MAX_SEQUENCE;

const isPositiveSequence = (value: unknown): value is number =>
  isSequence(value) && value > 0;

const isRunState = (value: unknown): value is RunState =>
  typeof value === "string" &&
  (ACTIVE_RUN_STATES.has(value as RunState) ||
    TERMINAL_RUN_STATES.has(value as RunState));

const isUsageState = (value: unknown): value is UsageState =>
  value === "RESERVED" ||
  value === "PENDING_RECONCILIATION" ||
  value === "RECONCILED";

const isResultState = (value: unknown): value is ResultState =>
  value === "NONE" || value === "COMPLETE" || value === "PARTIAL";

const hasCurrentVersion = (
  value: Record<string, unknown>,
): value is Record<string, unknown> & {
  contract_version: "1";
  schema_version: "1.0.0";
} =>
  value.contract_version === "1" && value.schema_version === "1.0.0";

const protocolFailure = (code = "PORTAL_PROTOCOL_ERROR") =>
  new PortalGatewayError({ code });

const isHistoryToken = (value: unknown): value is string =>
  typeof value === "string" &&
  value.length >= 1 &&
  value.length <= MAX_HISTORY_TOKEN_CHARACTERS;

const isOptionalHistoryToken = (value: unknown): value is string | null =>
  value === null || isHistoryToken(value);

export const parseRunSnapshot = (value: unknown): RunSnapshot => {
  if (
    !isRecord(value) ||
    !hasCurrentVersion(value) ||
    !isUuidV4(value.run_id) ||
    !isUuidV4(value.correlation_id) ||
    !isRunState(value.state) ||
    !isDateTime(value.deadline_at) ||
    !isSequence(value.last_sequence) ||
    !isUsageState(value.usage_state) ||
    !isResultState(value.result_state) ||
    !isDateTime(value.created_at) ||
    !isDateTime(value.updated_at) ||
    !(value.terminal_at === null || isDateTime(value.terminal_at))
  ) {
    throw protocolFailure();
  }
  return {
    contract_version: value.contract_version,
    schema_version: value.schema_version,
    run_id: value.run_id,
    correlation_id: value.correlation_id,
    state: value.state,
    deadline_at: value.deadline_at,
    last_sequence: value.last_sequence,
    usage_state: value.usage_state,
    result_state: value.result_state,
    created_at: value.created_at,
    updated_at: value.updated_at,
    terminal_at: value.terminal_at,
  };
};

const parseRunCommand = (value: unknown): RunCommand => {
  if (
    !isRecord(value) ||
    !hasCurrentVersion(value) ||
    !isUuidV4(value.run_id) ||
    !isRunState(value.state) ||
    !(
      value.outcome === "APPLIED" ||
      value.outcome === "NO_CHANGE_PENDING" ||
      value.outcome === "NO_CHANGE_TERMINAL"
    )
  ) {
    throw protocolFailure();
  }
  return {
    contract_version: value.contract_version,
    schema_version: value.schema_version,
    run_id: value.run_id,
    state: value.state,
    outcome: value.outcome,
  };
};

export const parseConversationPage = (value: unknown): ConversationPage => {
  if (
    !isRecord(value) ||
    !hasCurrentVersion(value) ||
    !hasExactKeys(value, [
      "contract_version",
      "schema_version",
      "items",
      "next_cursor",
    ]) ||
    !Array.isArray(value.items) ||
    value.items.length > MAX_HISTORY_PAGE_SIZE ||
    !isOptionalHistoryToken(value.next_cursor)
  ) {
    throw protocolFailure("HISTORY_PROTOCOL_ERROR");
  }
  const items = value.items.map((item): ConversationSummary => {
    if (
      !isRecord(item) ||
      !hasCurrentVersion(item) ||
      !hasExactKeys(item, [
        "contract_version",
        "schema_version",
        "conversation_id",
        "first_visible_sequence",
        "last_visible_sequence",
        "first_activity_at",
        "last_activity_at",
        "next_content_expires_at",
      ]) ||
      !isUuidV4(item.conversation_id) ||
      !isPositiveSequence(item.first_visible_sequence) ||
      !isPositiveSequence(item.last_visible_sequence) ||
      item.first_visible_sequence > item.last_visible_sequence ||
      !isAwareDateTime(item.first_activity_at) ||
      !isAwareDateTime(item.last_activity_at) ||
      !isAwareDateTime(item.next_content_expires_at) ||
      Date.parse(item.first_activity_at) > Date.parse(item.last_activity_at) ||
      Date.parse(item.next_content_expires_at) <=
        Date.parse(item.last_activity_at)
    ) {
      throw protocolFailure("HISTORY_PROTOCOL_ERROR");
    }
    return {
      contract_version: item.contract_version,
      schema_version: item.schema_version,
      conversation_id: item.conversation_id,
      first_visible_sequence: item.first_visible_sequence,
      last_visible_sequence: item.last_visible_sequence,
      first_activity_at: item.first_activity_at,
      last_activity_at: item.last_activity_at,
      next_content_expires_at: item.next_content_expires_at,
    };
  });
  const identities = new Set(items.map((item) => item.conversation_id));
  const ordering = items.map((item) => [
    Date.parse(item.last_activity_at),
    item.conversation_id,
  ] as const);
  if (
    identities.size !== items.length ||
    ordering.some((item, index) => {
      const previous = ordering[index - 1];
      return (
        previous !== undefined &&
        (item[0] > previous[0] ||
          (item[0] === previous[0] && item[1] >= previous[1]))
      );
    })
  ) {
    throw protocolFailure("HISTORY_PROTOCOL_ERROR");
  }
  return {
    contract_version: value.contract_version,
    schema_version: value.schema_version,
    items,
    next_cursor: value.next_cursor,
  };
};

const parseConversationEntry = (
  value: unknown,
  expectedConversationId: string,
): ConversationEntry => {
  if (!isRecord(value) || !hasCurrentVersion(value)) {
    throw protocolFailure("HISTORY_PROTOCOL_ERROR");
  }
  if (value.state === "REMOVED") {
    if (
      !hasExactKeys(value, [
        "contract_version",
        "schema_version",
        "state",
        "after_sequence",
        "before_sequence",
      ]) ||
      !isSequence(value.after_sequence) ||
      !isPositiveSequence(value.before_sequence) ||
      value.before_sequence < value.after_sequence + 2
    ) {
      throw protocolFailure("HISTORY_PROTOCOL_ERROR");
    }
    return {
      contract_version: value.contract_version,
      schema_version: value.schema_version,
      state: "REMOVED",
      after_sequence: value.after_sequence,
      before_sequence: value.before_sequence,
    };
  }
  if (
    !hasExactKeys(value, [
      "contract_version",
      "schema_version",
      "state",
      "entry_id",
      "conversation_id",
      "entry_sequence",
      "role",
      "content_kind",
      "content",
      "created_at",
      "content_expires_at",
    ]) ||
    value.state !== "AVAILABLE" ||
    !isUuidV4(value.entry_id) ||
    value.conversation_id !== expectedConversationId ||
    !isUuidV4(value.conversation_id) ||
    !isPositiveSequence(value.entry_sequence) ||
    !(value.role === "USER" || value.role === "ASSISTANT") ||
    !(
      value.content_kind === "MESSAGE" ||
      value.content_kind === "COUNTEROPINION_RESULT"
    ) ||
    typeof value.content !== "string" ||
    value.content.length === 0 ||
    hasUnpairedSurrogate(value.content) ||
    new TextEncoder().encode(value.content).byteLength >
      MAX_HISTORY_CONTENT_UTF8_BYTES ||
    !isAwareDateTime(value.created_at) ||
    !isAwareDateTime(value.content_expires_at) ||
    Date.parse(value.content_expires_at) <= Date.parse(value.created_at)
  ) {
    throw protocolFailure("HISTORY_PROTOCOL_ERROR");
  }
  return {
    contract_version: value.contract_version,
    schema_version: value.schema_version,
    state: "AVAILABLE",
    entry_id: value.entry_id,
    conversation_id: value.conversation_id,
    entry_sequence: value.entry_sequence,
    role: value.role,
    content_kind: value.content_kind,
    content: value.content,
    created_at: value.created_at,
    content_expires_at: value.content_expires_at,
  };
};

export const parseConversationEntriesPage = (
  value: unknown,
  expectedConversationId: string,
): ConversationEntriesPage => {
  if (
    !isUuidV4(expectedConversationId) ||
    !isRecord(value) ||
    !hasCurrentVersion(value) ||
    !hasExactKeys(value, [
      "contract_version",
      "schema_version",
      "conversation_id",
      "items",
      "next_cursor",
    ]) ||
    value.conversation_id !== expectedConversationId ||
    !Array.isArray(value.items) ||
    value.items.length > MAX_HISTORY_PAGE_SIZE * 2 ||
    !isOptionalHistoryToken(value.next_cursor)
  ) {
    throw protocolFailure("HISTORY_PROTOCOL_ERROR");
  }
  const items = value.items.map((item) =>
    parseConversationEntry(item, expectedConversationId),
  );
  let previousAvailable: number | null = null;
  let pendingGap: RemovedConversationEntry | null = null;
  const entryIds = new Set<string>();
  for (const item of items) {
    if (item.state === "REMOVED") {
      if (
        pendingGap !== null ||
        (previousAvailable !== null &&
          item.after_sequence !== previousAvailable)
      ) {
        throw protocolFailure("HISTORY_PROTOCOL_ERROR");
      }
      pendingGap = item;
      continue;
    }
    if (
      entryIds.has(item.entry_id) ||
      (pendingGap !== null &&
        item.entry_sequence !== pendingGap.before_sequence) ||
      (pendingGap === null &&
        previousAvailable !== null &&
        item.entry_sequence !== previousAvailable + 1)
    ) {
      throw protocolFailure("HISTORY_PROTOCOL_ERROR");
    }
    entryIds.add(item.entry_id);
    previousAvailable = item.entry_sequence;
    pendingGap = null;
  }
  if (pendingGap !== null) {
    throw protocolFailure("HISTORY_PROTOCOL_ERROR");
  }
  return {
    contract_version: value.contract_version,
    schema_version: value.schema_version,
    conversation_id: value.conversation_id,
    items,
    next_cursor: value.next_cursor,
  };
};

const parseConversationDeletion = (
  value: unknown,
  expectedEntryId: string,
): ConversationDeletion => {
  if (
    !isUuidV4(expectedEntryId) ||
    !isRecord(value) ||
    !hasCurrentVersion(value) ||
    !hasExactKeys(value, [
      "contract_version",
      "schema_version",
      "entry_id",
      "disposition",
    ]) ||
    value.entry_id !== expectedEntryId ||
    !(
      value.disposition === "REQUESTED" ||
      value.disposition === "ALREADY_REQUESTED"
    )
  ) {
    throw protocolFailure("HISTORY_PROTOCOL_ERROR");
  }
  return {
    contract_version: value.contract_version,
    schema_version: value.schema_version,
    entry_id: value.entry_id,
    disposition: value.disposition,
  };
};

const parseConversationResume = (
  value: unknown,
  expectedConversationId: string,
): ConversationResume => {
  if (
    !isUuidV4(expectedConversationId) ||
    !isRecord(value) ||
    !hasCurrentVersion(value) ||
    !hasExactKeys(value, [
      "contract_version",
      "schema_version",
      "resume_id",
      "conversation_id",
      "maximum_visible_sequence",
      "resume_expires_at",
      "authorization_status",
      "egress_status",
      "resume_token",
    ]) ||
    !isUuidV4(value.resume_id) ||
    value.conversation_id !== expectedConversationId ||
    !isPositiveSequence(value.maximum_visible_sequence) ||
    !isAwareDateTime(value.resume_expires_at) ||
    value.authorization_status !== "RECHECK_REQUIRED" ||
    value.egress_status !== "PENDING_RUN_POLICY" ||
    typeof value.resume_token !== "string" ||
    value.resume_token.length < 64 ||
    value.resume_token.length > MAX_HISTORY_TOKEN_CHARACTERS
  ) {
    throw protocolFailure("HISTORY_PROTOCOL_ERROR");
  }
  return {
    contract_version: value.contract_version,
    schema_version: value.schema_version,
    resume_id: value.resume_id,
    conversation_id: value.conversation_id,
    maximum_visible_sequence: value.maximum_visible_sequence,
    resume_expires_at: value.resume_expires_at,
    authorization_status: value.authorization_status,
    egress_status: value.egress_status,
    resume_token: value.resume_token,
  };
};

const parseJsonText = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    throw protocolFailure();
  }
};

const parseErrorCode = async (response: Response): Promise<string> => {
  try {
    const text = await response.text();
    if (text.length > MAX_ERROR_BODY_CHARACTERS) {
      return "PORTAL_REQUEST_FAILED";
    }
    const value = parseJsonText(text);
    if (
      isRecord(value) &&
      isRecord(value.error) &&
      typeof value.error.code === "string" &&
      /^[A-Z][A-Z0-9_]{1,63}$/.test(value.error.code)
    ) {
      return value.error.code;
    }
  } catch {
    return "PORTAL_REQUEST_FAILED";
  }
  return "PORTAL_REQUEST_FAILED";
};

const responseFailure = async (
  response: Response,
): Promise<PortalGatewayError> => {
  const code = await parseErrorCode(response);
  return new PortalGatewayError({
    code,
    httpStatus: response.status,
    retryable: response.status === 502 || response.status === 503,
    bootstrapInvalid:
      code === "CSRF_DENIED" || code === "SERVICE_UNAVAILABLE",
  });
};

const validateRunId = (runId: string): void => {
  if (!UUID_V4_PATTERN.test(runId)) {
    throw protocolFailure("INVALID_RUN_ID");
  }
};

const hasUnpairedSurrogate = (value: string): boolean => {
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit >= 0xd800 && codeUnit <= 0xdbff) {
      const nextCodeUnit = value.charCodeAt(index + 1);
      if (!(nextCodeUnit >= 0xdc00 && nextCodeUnit <= 0xdfff)) {
        return true;
      }
      index += 1;
    } else if (codeUnit >= 0xdc00 && codeUnit <= 0xdfff) {
      return true;
    }
  }
  return false;
};

const validatedRunSubmissionBody = (
  message: string,
  resumeToken?: string,
): string | null => {
  if (!message.trim() || hasUnpairedSurrogate(message)) {
    return null;
  }
  if (
    resumeToken !== undefined &&
    (resumeToken.length < 64 ||
      resumeToken.length > MAX_HISTORY_TOKEN_CHARACTERS ||
      hasUnpairedSurrogate(resumeToken))
  ) {
    return null;
  }
  const encodedMessage = new TextEncoder().encode(message);
  if (encodedMessage.byteLength > MAX_RUN_MESSAGE_UTF8_BYTES) {
    return null;
  }
  const body = JSON.stringify(
    resumeToken === undefined ? { message } : { message, resume_token: resumeToken },
  );
  const maximumRequestBytes =
    resumeToken === undefined
      ? MAX_RUN_REQUEST_UTF8_BYTES
      : MAX_RESUMED_RUN_REQUEST_UTF8_BYTES;
  return new TextEncoder().encode(body).byteLength <= maximumRequestBytes
    ? body
    : null;
};

export const isValidRunMessage = (message: string): boolean =>
  validatedRunSubmissionBody(message) !== null;

const serializeRunSubmission = (
  message: string,
  resumeToken?: string,
): string => {
  const body = validatedRunSubmissionBody(message, resumeToken);
  if (body === null) {
    throw protocolFailure("INVALID_REQUEST");
  }
  return body;
};

const parseStreamData = ({
  eventName,
  eventId,
  dataText,
  expectedRunId,
}: {
  eventName: string;
  eventId: string | null;
  dataText: string;
  expectedRunId: string;
}): PortalStreamEvent => {
  const value = parseJsonText(dataText);
  if (!isRecord(value) || !hasCurrentVersion(value)) {
    throw protocolFailure("STREAM_PROTOCOL_ERROR");
  }
  if (eventName === "stream-control") {
    const allowedCodes: StreamControlCode[] = [
      "STREAM_EXPIRED",
      "STREAM_BACKLOG_EXCEEDED",
      "STREAM_UNAVAILABLE",
      "STREAM_WINDOW_CLOSED",
    ];
    if (
      eventId !== null ||
      typeof value.code !== "string" ||
      !allowedCodes.includes(value.code as StreamControlCode)
    ) {
      throw protocolFailure("STREAM_PROTOCOL_ERROR");
    }
    return {
      kind: "stream-control",
      contract_version: value.contract_version,
      schema_version: value.schema_version,
      code: value.code as StreamControlCode,
    };
  }
  if (
    eventId === null ||
    !SEQUENCE_PATTERN.test(eventId) ||
    !isSequence(value.sequence) ||
    Number(eventId) !== value.sequence ||
    value.sequence === 0 ||
    value.run_id !== expectedRunId ||
    !isUuidV4(value.run_id) ||
    !isUuidV4(value.correlation_id) ||
    !isDateTime(value.occurred_at)
  ) {
    throw protocolFailure("STREAM_PROTOCOL_ERROR");
  }
  const common = {
    contract_version: value.contract_version,
    schema_version: value.schema_version,
    run_id: value.run_id,
    correlation_id: value.correlation_id,
    sequence: value.sequence,
    occurred_at: value.occurred_at,
  };
  if (eventName === "chunk" && typeof value.text === "string") {
    return { ...common, kind: "chunk", text: value.text };
  }
  if (
    eventName === "run-state" &&
    isRunState(value.state) &&
    isResultState(value.result_state) &&
    isUsageState(value.usage_state)
  ) {
    return {
      ...common,
      kind: "run-state",
      state: value.state,
      result_state: value.result_state,
      usage_state: value.usage_state,
    };
  }
  throw protocolFailure("STREAM_PROTOCOL_ERROR");
};

class SseDecoder {
  private buffer = "";
  private cursor: number;
  private readonly expectedRunId: string;

  constructor(expectedRunId: string, afterSequence: number) {
    validateRunId(expectedRunId);
    if (!isSequence(afterSequence)) {
      throw protocolFailure("INVALID_LAST_EVENT_ID");
    }
    this.expectedRunId = expectedRunId;
    this.cursor = afterSequence;
  }

  append(text: string): PortalStreamEvent[] {
    this.buffer += text;
    if (this.buffer.length > MAX_SSE_BUFFER_CHARACTERS) {
      throw protocolFailure("STREAM_BUFFER_EXCEEDED");
    }
    const events: PortalStreamEvent[] = [];
    while (true) {
      const delimiter = this.findDelimiter();
      if (delimiter === null) {
        return events;
      }
      const frame = this.buffer.slice(0, delimiter.index);
      this.buffer = this.buffer.slice(
        delimiter.index + delimiter.length,
      );
      const parsed = this.parseFrame(frame.replaceAll("\r\n", "\n"));
      if (parsed === null) {
        continue;
      }
      if (parsed.kind === "stream-control") {
        events.push(parsed);
        continue;
      }
      if (parsed.sequence <= this.cursor) {
        continue;
      }
      if (parsed.sequence !== this.cursor + 1) {
        throw protocolFailure("STREAM_SEQUENCE_GAP");
      }
      this.cursor = parsed.sequence;
      events.push(parsed);
    }
  }

  finish(): void {
    if (this.buffer.trim().length > 0) {
      throw protocolFailure("STREAM_TRUNCATED");
    }
  }

  private findDelimiter(): { index: number; length: number } | null {
    const lineFeed = this.buffer.indexOf("\n\n");
    const carriageReturn = this.buffer.indexOf("\r\n\r\n");
    if (lineFeed < 0 && carriageReturn < 0) {
      return null;
    }
    if (
      carriageReturn >= 0 &&
      (lineFeed < 0 || carriageReturn < lineFeed)
    ) {
      return { index: carriageReturn, length: 4 };
    }
    return { index: lineFeed, length: 2 };
  }

  private parseFrame(frame: string): PortalStreamEvent | null {
    if (!frame || frame.startsWith(":")) {
      return null;
    }
    let eventName = "";
    let eventId: string | null = null;
    const dataLines: string[] = [];
    for (const line of frame.split("\n")) {
      if (!line || line.startsWith(":")) {
        continue;
      }
      const separator = line.indexOf(":");
      const field = separator < 0 ? line : line.slice(0, separator);
      let value = separator < 0 ? "" : line.slice(separator + 1);
      if (value.startsWith(" ")) {
        value = value.slice(1);
      }
      if (field === "event") {
        if (eventName) {
          throw protocolFailure("STREAM_PROTOCOL_ERROR");
        }
        eventName = value;
      } else if (field === "id") {
        if (eventId !== null) {
          throw protocolFailure("STREAM_PROTOCOL_ERROR");
        }
        eventId = value;
      } else if (field === "data") {
        dataLines.push(value);
      }
    }
    if (!eventName || dataLines.length === 0) {
      throw protocolFailure("STREAM_PROTOCOL_ERROR");
    }
    return parseStreamData({
      eventName,
      eventId,
      dataText: dataLines.join("\n"),
      expectedRunId: this.expectedRunId,
    });
  }
}

export const decodeSseText = (
  text: string,
  runId: string,
  afterSequence = 0,
): PortalStreamEvent[] => {
  const decoder = new SseDecoder(runId, afterSequence);
  const events = decoder.append(text);
  decoder.finish();
  return events;
};

export class BrowserPortalGateway implements PortalGateway {
  private csrfToken: string | null;
  private readonly fetch: FetchLike;

  constructor(csrfToken: string, fetchImplementation: FetchLike = fetch) {
    if (!CSRF_TOKEN_PATTERN.test(csrfToken)) {
      throw protocolFailure("INVALID_BOOTSTRAP");
    }
    this.csrfToken = csrfToken;
    this.fetch = fetchImplementation.bind(globalThis);
  }

  async submitRun(
    message: string,
    signal: AbortSignal,
    resumeToken?: string,
  ): Promise<RunCommand> {
    if (this.csrfToken === null) {
      throw new PortalGatewayError({
        code: "BOOTSTRAP_UNAVAILABLE",
        bootstrapInvalid: true,
      });
    }
    const response = await this.request("/api/v1/runs", {
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
      },
      body: serializeRunSubmission(message, resumeToken),
    });
    const command = parseRunCommand(await response.json());
    if (command.state !== "QUEUED" || command.outcome !== "APPLIED") {
      throw protocolFailure();
    }
    return command;
  }

  async readRun(runId: string, signal: AbortSignal): Promise<RunSnapshot> {
    validateRunId(runId);
    const response = await this.request(
      `/api/v1/runs/${encodeURIComponent(runId)}`,
      { method: "GET", signal },
    );
    return parseRunSnapshot(await response.json());
  }

  async readActiveRun(
    signal: AbortSignal,
  ): Promise<RunSnapshot | null> {
    const response = await this.request(
      "/api/v1/runs/active",
      { method: "GET", signal },
      new Set([204]),
    );
    if (response.status === 204) {
      return null;
    }
    return parseRunSnapshot(await response.json());
  }

  async *streamRun(
    runId: string,
    afterSequence: number,
    signal: AbortSignal,
  ): AsyncIterable<PortalStreamEvent> {
    validateRunId(runId);
    if (!isSequence(afterSequence)) {
      throw protocolFailure("INVALID_LAST_EVENT_ID");
    }
    const headers = new Headers();
    if (afterSequence > 0) {
      headers.set("Last-Event-ID", String(afterSequence));
    }
    const response = await this.request(
      `/api/v1/runs/${encodeURIComponent(runId)}/events`,
      { method: "GET", headers, signal },
      new Set([204]),
    );
    if (response.status === 204) {
      return;
    }
    if (!response.body) {
      throw protocolFailure("STREAM_UNAVAILABLE");
    }

    const reader = response.body.getReader();
    const textDecoder = new TextDecoder("utf-8", { fatal: true });
    const decoder = new SseDecoder(runId, afterSequence);
    try {
      while (true) {
        const item = await reader.read();
        if (item.done) {
          const trailing = textDecoder.decode();
          if (trailing) {
            for (const event of decoder.append(trailing)) {
              yield event;
            }
          }
          decoder.finish();
          return;
        }
        const text = textDecoder.decode(item.value, { stream: true });
        for (const event of decoder.append(text)) {
          yield event;
        }
      }
    } catch (error) {
      if (
        error instanceof PortalGatewayError ||
        error instanceof DOMException
      ) {
        throw error;
      }
      throw protocolFailure("STREAM_PROTOCOL_ERROR");
    } finally {
      reader.releaseLock();
    }
  }

  async revokeRun(
    runId: string,
    signal: AbortSignal,
  ): Promise<RunCommand> {
    validateRunId(runId);
    if (this.csrfToken === null) {
      throw new PortalGatewayError({
        code: "BOOTSTRAP_UNAVAILABLE",
        bootstrapInvalid: true,
      });
    }
    const response = await this.request(
      `/api/v1/runs/${encodeURIComponent(runId)}/revoke`,
      {
        method: "POST",
        signal,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: "{}",
      },
    );
    return parseRunCommand(await response.json());
  }

  async listConversations(
    cursor: string | null,
    signal: AbortSignal,
  ): Promise<ConversationPage> {
    if (cursor !== null && !isHistoryToken(cursor)) {
      throw protocolFailure("INVALID_HISTORY_CURSOR");
    }
    const query = new URLSearchParams({ page_size: "20" });
    if (cursor !== null) {
      query.set("cursor", cursor);
    }
    const response = await this.request(
      `/api/v1/conversations?${query.toString()}`,
      { method: "GET", signal },
    );
    return parseConversationPage(await response.json());
  }

  async listConversationEntries(
    conversationId: string,
    cursor: string | null,
    signal: AbortSignal,
  ): Promise<ConversationEntriesPage> {
    if (!isUuidV4(conversationId)) {
      throw protocolFailure("INVALID_CONVERSATION_ID");
    }
    if (cursor !== null && !isHistoryToken(cursor)) {
      throw protocolFailure("INVALID_HISTORY_CURSOR");
    }
    const query = new URLSearchParams({ page_size: "20" });
    if (cursor !== null) {
      query.set("cursor", cursor);
    }
    const response = await this.request(
      `/api/v1/conversations/${encodeURIComponent(conversationId)}/entries?${query.toString()}`,
      { method: "GET", signal },
    );
    return parseConversationEntriesPage(
      await response.json(),
      conversationId,
    );
  }

  async deleteConversationEntry(
    entryId: string,
    signal: AbortSignal,
  ): Promise<ConversationDeletion> {
    if (!isUuidV4(entryId)) {
      throw protocolFailure("INVALID_HISTORY_ENTRY_ID");
    }
    const response = await this.mutateEmpty(
      `/api/v1/conversation-entries/${encodeURIComponent(entryId)}/delete`,
      signal,
    );
    return parseConversationDeletion(await response.json(), entryId);
  }

  async prepareConversationResume(
    conversationId: string,
    signal: AbortSignal,
  ): Promise<ConversationResume> {
    if (!isUuidV4(conversationId)) {
      throw protocolFailure("INVALID_CONVERSATION_ID");
    }
    const response = await this.mutateEmpty(
      `/api/v1/conversations/${encodeURIComponent(conversationId)}/resume`,
      signal,
    );
    return parseConversationResume(await response.json(), conversationId);
  }

  private async mutateEmpty(
    path: string,
    signal: AbortSignal,
  ): Promise<Response> {
    if (this.csrfToken === null) {
      throw new PortalGatewayError({
        code: "BOOTSTRAP_UNAVAILABLE",
        bootstrapInvalid: true,
      });
    }
    return this.request(path, {
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
      },
      body: "{}",
    });
  }

  private async request(
    path: string,
    init: RequestInit,
    additionalSuccess = new Set<number>(),
  ): Promise<Response> {
    let response: Response;
    try {
      response = await this.fetch(path, {
        ...init,
        credentials: "same-origin",
        cache: "no-store",
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }
      throw new PortalGatewayError({
        code: "PORTAL_NETWORK_ERROR",
        retryable: true,
      });
    }
    if (!response.ok && !additionalSuccess.has(response.status)) {
      const failure = await responseFailure(response);
      if (failure.bootstrapInvalid) {
        this.csrfToken = null;
      }
      throw failure;
    }
    return response;
  }
}

export type BrowserBootstrap =
  | {
      outcome: "READY";
      gateway: BrowserPortalGateway;
      runId: string | null;
      correlationId: null;
    }
  | {
      outcome: "AUTHENTICATION_REQUIRED";
      gateway: null;
      runId: string | null;
      correlationId: string;
    }
  | {
      outcome: "UNAVAILABLE";
      gateway: null;
      runId: string | null;
      correlationId: string | null;
    };

export const readBrowserBootstrap = (
  locationValue: Location,
  fetchImplementation: FetchLike = fetch,
): Promise<BrowserBootstrap> => {
  const candidateRunId = new URL(locationValue.href).searchParams.get("run");
  const runId =
    candidateRunId && UUID_V4_PATTERN.test(candidateRunId)
      ? candidateRunId
      : null;
  return fetchBrowserBootstrap(fetchImplementation, runId);
};

const fetchBrowserBootstrap = async (
  fetchImplementation: FetchLike,
  runId: string | null,
): Promise<BrowserBootstrap> => {
  let response: Response;
  try {
    response = await fetchImplementation.call(
      globalThis,
      CSRF_BOOTSTRAP_PATH,
      {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
        redirect: "error",
        signal: AbortSignal.timeout(
          BROWSER_BOOTSTRAP_TIMEOUT_MILLISECONDS,
        ),
      },
    );
  } catch {
    return unavailableBootstrap(runId);
  }
  if (response.status === 403 || response.status === 503) {
    const failure = await readBrowserBootstrapFailure(response);
    if (
      failure?.code === "AUTHENTICATION_REQUIRED" &&
      response.status === 403
    ) {
      return {
        outcome: "AUTHENTICATION_REQUIRED",
        gateway: null,
        runId,
        correlationId: failure.correlationId,
      };
    }
    return unavailableBootstrap(
      runId,
      failure?.code === "SERVICE_UNAVAILABLE" &&
        response.status === 503
        ? failure.correlationId
        : null,
    );
  }
  const csrf = response.headers.get("X-CSRF-Token");
  if (
    response.status !== 204 ||
    response.headers.has("Content-Length") ||
    response.headers.has("Content-Type") ||
    csrf === null ||
    !CSRF_TOKEN_PATTERN.test(csrf)
  ) {
    return unavailableBootstrap(runId);
  }
  try {
    const body = await readBoundedBootstrapBody(response, 0, 1);
    if (body === null || body.byteLength !== 0) {
      return unavailableBootstrap(runId);
    }
  } catch {
    return unavailableBootstrap(runId);
  }
  return {
    outcome: "READY",
    gateway: new BrowserPortalGateway(csrf, fetchImplementation),
    runId,
    correlationId: null,
  };
};

const unavailableBootstrap = (
  runId: string | null,
  correlationId: string | null = null,
): BrowserBootstrap => ({
  outcome: "UNAVAILABLE",
  gateway: null,
  runId,
  correlationId,
});

const readBrowserBootstrapFailure = async (
  response: Response,
): Promise<{
  code: "AUTHENTICATION_REQUIRED" | "SERVICE_UNAVAILABLE";
  correlationId: string;
} | null> => {
  const correlationId = response.headers.get("X-Correlation-ID");
  const expectedCode =
    response.status === 403
      ? "AUTHENTICATION_REQUIRED"
      : response.status === 503
        ? "SERVICE_UNAVAILABLE"
        : null;
  if (
    expectedCode === null ||
    response.headers.get("Content-Type") !== "application/json" ||
    response.headers.get("Cache-Control") !== "no-store" ||
    response.headers.get("X-Content-Type-Options") !== "nosniff" ||
    response.headers.get("Referrer-Policy") !== "no-referrer" ||
    response.headers.get("Cross-Origin-Resource-Policy") !==
      "same-origin" ||
    response.headers.get("X-Frame-Options") !== "DENY" ||
    !isCanonicalUuidV4(correlationId)
  ) {
    return null;
  }

  const body = await readBoundedBootstrapBody(
    response,
    MAX_BOOTSTRAP_ERROR_BODY_BYTES,
    MAX_BOOTSTRAP_ERROR_BODY_CHUNKS,
  );
  if (body === null) {
    return null;
  }
  let value: unknown;
  try {
    value = JSON.parse(
      new TextDecoder("utf-8", { fatal: true }).decode(body),
    );
  } catch {
    return null;
  }
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["error"]) ||
    !isRecord(value.error) ||
    !hasExactKeys(value.error, ["code", "correlation_id"]) ||
    value.error.code !== expectedCode ||
    value.error.correlation_id !== correlationId
  ) {
    return null;
  }
  return { code: expectedCode, correlationId };
};

const isCanonicalUuidV4 = (value: unknown): value is string =>
  isUuidV4(value) && value === value.toLowerCase();

const readBoundedBootstrapBody = async (
  response: Response,
  maximumBytes: number,
  maximumChunks: number,
): Promise<Uint8Array | null> => {
  const contentLength = response.headers.get("Content-Length");
  if (
    contentLength !== null &&
    (!/^(0|[1-9][0-9]{0,6})$/.test(contentLength) ||
      Number(contentLength) > maximumBytes)
  ) {
    return null;
  }
  if (response.body === null) {
    return new Uint8Array();
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let totalBytes = 0;
  let chunkCount = 0;
  try {
    for (;;) {
      const result = await reader.read();
      if (result.done) {
        break;
      }
      chunkCount += 1;
      totalBytes += result.value.byteLength;
      if (
        chunkCount > maximumChunks ||
        totalBytes > maximumBytes
      ) {
        await reader.cancel();
        return null;
      }
      chunks.push(result.value);
    }
  } catch {
    return null;
  } finally {
    reader.releaseLock();
  }

  const combined = new Uint8Array(totalBytes);
  let offset = 0;
  for (const chunk of chunks) {
    combined.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return combined;
};
